import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import type { UUID } from '@aline/common';
import { ALINE_TOOL_NAMES } from '@aline/common';
import { gql } from '@apollo/client';
import { Fab, Popper } from '@mui/material';
import clsx from 'clsx';
import Color from 'color';
import { Helmet } from 'react-helmet';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { DownArrowIcon } from 'src/components/icons/DownArrowIcon';
import type { NotFoundAction } from 'src/components/not-found/NotFoundState';
import { NOT_FOUND_ENTITIES, NotFoundState } from 'src/components/not-found/NotFoundState';
import { Apollo } from 'src/gql/generated';
import { useMaybeDocumentParams } from 'src/helpers';
import { useAlineMeasure, useGetChatThread } from 'src/hooks';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { hoverButtonOpacity } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useStickToBottom } from 'use-stick-to-bottom';
import { useSnapshot } from 'valtio';

import type { DocumentMentionElement } from './ChatEditor/ChatEditorMention/utils';
import type { PinnedSkill } from './ChatEditor/ChatEditorSkill/utils';
import { ChatLoadErrorState } from './ChatLoadErrorState';
import {
  ChatPanelAssistantMessage,
  DocumentExtractionWaitBanner,
} from './ChatPanelAssistantMessage';
import { ChatPanelHeader } from './ChatPanelHeader';
import type { ChatPanelInputRef } from './ChatPanelInput';
import { ChatPanelInput } from './ChatPanelInput';
import { ChatPanelUserMessage } from './ChatPanelUserMessage';
import { getChatNotFoundActions } from './get-chat-not-found-actions';
import { getIsWaitingForDocumentExtraction } from './get-is-waiting-for-document-extraction';
import { ThreadDeliverablesBar } from './ThreadDeliverablesBar/ThreadDeliverablesBar';
import type { PromptInsertion } from './ThreadDeliverablesBar/ThreadDeliverablesBar';
import { ApprovalCard, AskUserQuestionsCard } from '../AskUser/AskUser';
import { ConnectExternalAccountCard } from '../AskUser/ConnectExternalAccountCard';
import { EnableExternalToolsCard } from '../AskUser/EnableExternalToolsCard';
import { QuestionRunApprovalCard } from '../AskUser/question-plan/QuestionRunApprovalCard';
import { ReportPlanApprovalCard } from '../AskUser/ReportPlanApprovalCard';
import { usePendingInteraction } from '../AskUser/usePendingInteraction';
import type { PendingInteraction } from '../AskUser/usePendingInteraction';
import { WorkflowCollectIntakeFormCard } from '../AskUser/WorkflowCollectIntakeFormCard';
import {
  WorkflowCollectLaunchInfoCard,
  WorkflowLaunchTemplateApprovalCard,
} from '../AskUser/WorkflowCollectLaunchInfoCard';
import { extractMentionEntityIdsFromMessages } from '../chat-hooks/extractMentionEntityIdsFromMessages';
import { useDocPlaybookRunSubscriptions } from '../chat-hooks/useDocPlaybookRunSubscriptions';
import type { InsertSelectionMentionV3Fn } from '../chat-hooks/useInsertSelectionMentionV3';
import {
  AccessRequestStatusContext,
  MentionEntityContext,
  useAccessRequestStatus,
} from '../chat-hooks/useMentionAccessContext';
import type {
  AccessRequestInfo,
  MentionEntityContextValue,
} from '../chat-hooks/useMentionAccessContext';
import { useMentionEntitiesQuery } from '../chat-hooks/useMentionEntitiesQuery';
import { usePlaybookGenerationSubscriptions } from '../chat-hooks/usePlaybookGenerationSubscriptions';
import type { SelectedPlaybookRun } from '../chat-hooks/useSelectedPlaybookRun';
import { SelectedPlaybookRunContext } from '../chat-hooks/useSelectedPlaybookRun';
import { useSendUserMessage } from '../chat-hooks/useSendUserMessage';
import { $chatCallbacks, $chatThreadState } from '../chat-state';
import { ChatAdapterProvider } from '../ChatPanelMarkdown';
import { ChatPlaybookRunPanel } from '../playbook/ChatPlaybookRunPanel';

const maxWidthPx = 720;
const useStyles = makeStyles()(theme => ({
  bottomArtifacts: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    /**
     * The Popper is sized to the input anchor, so a slight percentage inset
     * keeps the stacked question card and deliverables bar tucked inside the
     * composer border while leaving the cards as wide as possible on narrow
     * (docked) chats.
     */
    margin: '0 auto',
    maxWidth: maxWidthPx * 0.925,
    overflowY: 'auto',
    width: '92.5%',
  },
  bottomDeliverablesPopper: {
    /**
     * Popper sits above the app bar so expanded approval/question cards
     * are not hidden by the main navigation when they grow upward.
     */
    zIndex: theme.zIndex.appBar + 1,
  },
  bottomInput: {
    maxWidth: maxWidthPx,
    width: '100%',
  },
  bottomInputContainer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.5, 2),
    width: '100%',
  },
  documentExtractionWaitBanner: {
    maxWidth: maxWidthPx,
    width: '100%',
  },
  documentExtractionWaitContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  header: {
    width: '100%',
  },
  headerContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  logo: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  messagesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
    maxWidth: maxWidthPx,
    width: '100%',
  },
  newChatWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  newChatWrapperBottom: {
    marginTop: 'auto',
  },
  newChatWrapperCentered: {
    marginBottom: 'auto',
    marginTop: 'auto',
    paddingBottom: 'max(24px, 10vh)',
    paddingTop: 24,
  },
  playbookRunPage: {
    margin: '0 auto',
    maxWidth: maxWidthPx,
    width: '100%',
  },
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  scrollContainer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  scrollToBottomButton: {
    '&:hover': {
      // Creates a hex color equivalent to theme.appColors.hoverBackground but w/o transparency...
      backgroundColor: Color(theme.appColors.primary)
        .mix(Color(theme.palette.common.white), 1 - hoverButtonOpacity)
        .hex(),
    },
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    right: '50%',
    transform: 'translateX(50%)',
    zIndex: theme.zIndex.appBar + 2,
  },
}));

/** Messages type from useGetChatThread hook */
type ChatMessages = ReturnType<typeof useGetChatThread>['messages'];

interface ChatScrollContentProps {
  accessRequestStatusMap: Map<string, Apollo.AccessRequestStatus>;
  document: Apollo.GetChatDocumentQuery['document'] | null;
  documentId: string | undefined;
  hasLoadError: boolean;
  inputRef: React.RefObject<ChatPanelInputRef | null>;
  isNotFound: boolean;
  logoClassName: string;
  mentionEntityContextValue: MentionEntityContextValue;
  messages: ChatMessages;
  messagesContainerClassName: string;
  newChatWrapperBottomClassName: string;
  newChatWrapperCenteredClassName: string;
  newChatWrapperClassName: string;
  notFoundActions: readonly NotFoundAction[] | undefined;
  onNavigateToThread: (threadId: string) => void;
  onSubmit: (
    text: string,
    skills: PinnedSkill[],
    onConfirmed?: (confirmedThreadId?: string) => void
  ) => Promise<boolean>;
  refetchAccessRequestStatus: () => void;
  threadId: UUID | null;
  updateAccessRequestStatus: (newStatuses: AccessRequestInfo[]) => void;
  hideFeatureCards?: boolean;
  pinInputToBottom?: boolean;
  visibleContext?: Apollo.AgentVisibleContextInput;
}

const ChatScrollContent = React.forwardRef<HTMLDivElement, ChatScrollContentProps>(
  function ChatScrollContent(
    {
      accessRequestStatusMap,
      document,
      documentId,
      hasLoadError,
      hideFeatureCards,
      inputRef,
      isNotFound,
      logoClassName,
      mentionEntityContextValue,
      messages,
      messagesContainerClassName,
      newChatWrapperBottomClassName,
      newChatWrapperCenteredClassName,
      newChatWrapperClassName,
      notFoundActions,
      onNavigateToThread,
      onSubmit,
      pinInputToBottom,
      refetchAccessRequestStatus,
      threadId,
      updateAccessRequestStatus,
      visibleContext,
    },
    ref
  ) {
    const accessRequestStatusContextValue = useMemo(
      () => ({
        refetch: refetchAccessRequestStatus,
        statusMap: accessRequestStatusMap,
        updateStatus: updateAccessRequestStatus,
      }),
      [accessRequestStatusMap, refetchAccessRequestStatus, updateAccessRequestStatus]
    );

    /**
     * Checked before the thread branch: a routed thread that is missing or
     * inaccessible still has a `threadId`, so without this the panel renders an
     * empty message list that looks like a working, empty chat.
     */
    if (isNotFound)
      return <NotFoundState ref={ref} actions={notFoundActions} entity={NOT_FOUND_ENTITIES.chat} />;

    // Same reason for a load that failed any other way.
    if (hasLoadError) return <ChatLoadErrorState ref={ref} />;

    // Existing thread - show messages
    if (threadId) {
      return (
        <div ref={ref} className={messagesContainerClassName}>
          <MentionEntityContext.Provider value={mentionEntityContextValue}>
            <AccessRequestStatusContext.Provider value={accessRequestStatusContextValue}>
              <ChatAdapterProvider threadId={threadId}>
                {messages.map(message => {
                  switch (message.role) {
                    case Apollo.AgentMessageRole.User:
                      return <ChatPanelUserMessage key={message.id} message={message} />;
                    case Apollo.AgentMessageRole.Assistant:
                      return (
                        <ChatPanelAssistantMessage
                          key={message.id}
                          documentInView={document}
                          message={message}
                          threadId={threadId}
                        />
                      );
                  }
                })}
              </ChatAdapterProvider>
            </AccessRequestStatusContext.Provider>
          </MentionEntityContext.Provider>
        </div>
      );
    }

    // New chat - show input
    const wrapperClassName = clsx(
      newChatWrapperClassName,
      pinInputToBottom
        ? newChatWrapperBottomClassName
        : !documentId && !hideFeatureCards && newChatWrapperCenteredClassName
    );

    const showLogo = !documentId && !pinInputToBottom && !hideFeatureCards;

    return (
      <div className={wrapperClassName} data-picker-placement={showLogo ? 'bottom' : undefined}>
        {showLogo && (
          <div className={logoClassName}>
            <AlineLogo width={320} />
          </div>
        )}
        <div ref={ref} className={messagesContainerClassName}>
          <ChatPanelInput
            ref={inputRef}
            messages={messages}
            threadId={null}
            visibleContext={visibleContext}
            onNavigateToThread={onNavigateToThread}
            onSubmit={onSubmit}
          />
        </div>
      </div>
    );
  }
);

/**
 * Body of the above-input popper. A pending interaction (tool approval or
 * askUser card) takes precedence over the artifacts bar — it is ephemeral and
 * blocks the turn, so it renders alone; the deliverables bar returns once the
 * interaction resolves.
 */
const ChatPanelPopperBody = React.forwardRef<
  HTMLDivElement,
  {
    artifacts: React.ComponentProps<typeof ThreadDeliverablesBar>['artifacts'];
    className: string;
    maxHeight: number | null;
    onInsertIntoPrompt: (insertion: PromptInsertion) => void;
    pendingInteraction: PendingInteraction | null;
    threadId: UUID;
  }
>(function ChatPanelPopperBody(
  { artifacts, className, maxHeight, onInsertIntoPrompt, pendingInteraction, threadId },
  ref
) {
  if (pendingInteraction) {
    return (
      <div
        ref={ref}
        className={className}
        style={maxHeight ? { maxHeight, overflow: 'hidden' } : undefined}
      >
        <ChatAdapterProvider threadId={threadId}>
          <PendingInteractionCard interaction={pendingInteraction} maxHeight={maxHeight} />
        </ChatAdapterProvider>
      </div>
    );
  }

  if (!artifacts.length) return null;

  return (
    <div ref={ref} className={className} style={maxHeight ? { maxHeight } : undefined}>
      <ThreadDeliverablesBar artifacts={artifacts} onInsertIntoPrompt={onInsertIntoPrompt} />
    </div>
  );
});

function PendingInteractionCard({
  interaction,
  maxHeight,
}: {
  interaction: PendingInteraction;
  maxHeight: number | null;
}) {
  if (interaction.kind === 'connectExternalAccount')
    return (
      <ConnectExternalAccountCard
        maxHeight={maxHeight}
        messageId={interaction.messageId}
        queuePosition={interaction.queuePosition}
        toolCallIds={interaction.toolCallIds}
        toolkit={interaction.toolkit}
      />
    );

  if (interaction.kind === 'enableExternalTools')
    return (
      <EnableExternalToolsCard
        maxHeight={maxHeight}
        messageId={interaction.messageId}
        queuePosition={interaction.queuePosition}
        requests={interaction.requests}
        toolkit={interaction.toolkit}
        unionSlugs={interaction.unionSlugs}
      />
    );

  if (interaction.kind === 'askUser')
    return (
      <AskUserQuestionsCard
        maxHeight={maxHeight}
        messageId={interaction.messageId}
        questions={interaction.questions}
        title={interaction.title}
        toolCallId={interaction.toolCallId}
      />
    );

  if (interaction.kind === 'collectIntakeForm')
    return (
      <WorkflowCollectIntakeFormCard
        maxHeight={maxHeight}
        messageId={interaction.messageId}
        suggestedValues={interaction.suggestedValues}
        templateWorkflowId={interaction.templateWorkflowId}
        toolCallId={interaction.toolCallId}
      />
    );

  if (interaction.kind === 'collectLaunchInfo')
    return (
      <WorkflowCollectLaunchInfoCard
        maxHeight={maxHeight}
        messageId={interaction.messageId}
        suggestedAlineFieldValues={interaction.suggestedAlineFieldValues}
        suggestedIntakeValues={interaction.suggestedIntakeValues}
        suggestedRoleAssignments={interaction.suggestedRoleAssignments}
        templateWorkflowId={interaction.templateWorkflowId}
        toolCallId={interaction.toolCallId}
      />
    );

  if (interaction.toolName === ALINE_TOOL_NAMES.workflowLaunchTemplate)
    return (
      <WorkflowLaunchTemplateApprovalCard
        approvalId={interaction.approvalId}
        input={interaction.input}
        maxHeight={maxHeight}
        messageId={interaction.messageId}
      />
    );

  if (interaction.toolName === ALINE_TOOL_NAMES.createReportTool)
    return (
      <ReportPlanApprovalCard
        approvalId={interaction.approvalId}
        input={interaction.input}
        maxHeight={maxHeight}
        messageId={interaction.messageId}
      />
    );

  if (interaction.toolName === ALINE_TOOL_NAMES.runExtractionQuestionsTool)
    return (
      <QuestionRunApprovalCard
        approvalId={interaction.approvalId}
        input={interaction.input}
        maxHeight={maxHeight}
        messageId={interaction.messageId}
      />
    );

  return (
    <ApprovalCard
      approvalId={interaction.approvalId}
      input={interaction.input}
      maxHeight={maxHeight}
      messageId={interaction.messageId}
      toolName={interaction.toolName}
    />
  );
}

/**
 * Chat panel component.
 */
export function ChatPanel({
  className,
  hideFeatureCards,
  hideThreadHeader = false,
  mode = 'page',
  onClose,
  onCreateNewChat,
  onNavigateToThread,
  onSelectedPlaybookRunChange,
  onThreadHeaderGoBack,
  pinInputToBottom,
  registerGlobalCallbacks,
  scrollContainerClassName,
  selectedPlaybookRun,
  threadId,
  visibleContext,
}: {
  onNavigateToThread: (threadId: string) => void;
  /**
   * Playbook run drill-in state is always owned by the host so it can scope
   * the selection (e.g. clear it on thread changes). Hosts that don't support
   * the drill-in pass null and a no-op.
   */
  onSelectedPlaybookRunChange: (run: SelectedPlaybookRun | null) => void;
  selectedPlaybookRun: SelectedPlaybookRun | null;
  threadId: UUID | null;
  className?: string;
  hideFeatureCards?: boolean;
  hideThreadHeader?: boolean;
  mode?: 'drawer' | 'page';
  /**
   * Dismisses the panel. Docked hosts pass this, and a missing thread then
   * offers panel exits instead of Go Home, which would leave the document or
   * workflow underneath. The full-page chat leaves it unset.
   */
  onClose?: () => void;
  onCreateNewChat?: () => void;
  onThreadHeaderGoBack?: () => void;
  pinInputToBottom?: boolean;
  /**
   * Register this panel's input as the global target for out-of-tree actions
   * (repository "Add to Chat", editor selection ribbon). Only the singleton
   * main chat host should opt in — contextual chats like workflow panels
   * should not steal those actions.
   */
  registerGlobalCallbacks?: boolean;
  scrollContainerClassName?: string;
  visibleContext?: Apollo.AgentVisibleContextInput;
}): JSX.Element {
  const { classes } = useStyles();
  const state = useSnapshot($chatThreadState);
  const { updateActiveTabTitle } = useTabContext();
  const { document, documentId } = useMaybeDocument();
  const topInputRef = useRef<ChatPanelInputRef>(null);
  const bottomInputRef = useRef<ChatPanelInputRef>(null);
  const notFoundActions = useMemo(
    () => getChatNotFoundActions({ onClose, onCreateNewChat, onGoBack: onThreadHeaderGoBack }),
    [onClose, onCreateNewChat, onThreadHeaderGoBack]
  );
  /**
   * Deleting the thread the panel is showing evicts the cache entry but leaves
   * the panel pointed at it, so the next refetch answers NOT_FOUND and the user
   * who just pressed Delete is told it "may have been deleted". Reset to the
   * host's new-chat state instead; embedded hosts that only offer a way back
   * (workflow thread mode) fall through to that.
   */
  const handleThreadDeleted = useCallback(
    (deletedThreadId: UUID) => {
      if (deletedThreadId !== threadId) return;
      (onCreateNewChat ?? onThreadHeaderGoBack)?.();
    },
    [onCreateNewChat, onThreadHeaderGoBack, threadId]
  );
  const { hasLoadError, isNotFound, messages, thread } = useGetChatThread(threadId, {
    fetchPolicy: 'cache-and-network',
    /**
     * Apollo Client v4 keeps `nextFetchPolicy` applied across variable
     * changes, so a plain 'cache-first' string skips the network fetch
     * when threadId switches to an uncached thread, leaving the panel
     * blank. The function form restores 'cache-and-network' on
     * variable changes while keeping 'cache-first' for refetch/poll.
     */
    nextFetchPolicy: (_currentFetchPolicy, { reason }) =>
      reason === 'variables-changed' ? 'cache-and-network' : 'cache-first',
  });
  // A thread we could not load gets no composer or deliverables: there is nothing to send to.
  const isThreadUnavailable = isNotFound || hasLoadError;

  // Extract document IDs from all messages and query their access status
  const messageMentionEntityIds = useMemo(
    () => extractMentionEntityIdsFromMessages(messages),
    [messages]
  );
  const { inaccessibleDocumentIds, mentionEntityContextValue } =
    useMentionEntitiesQuery(messageMentionEntityIds);
  const {
    refetch: refetchAccessRequestStatus,
    statusMap: accessRequestStatusMap,
    updateStatus: updateAccessRequestStatus,
  } = useAccessRequestStatus(inaccessibleDocumentIds);

  const { contentRef, isAtBottom, scrollRef, scrollToBottom, stopScroll } = useStickToBottom();
  const [setBottomContainerRef, bottomContainerMeasure] = useAlineMeasure<HTMLDivElement>();
  const [setBottomInputMeasureRef, bottomInputMeasure, bottomInputElement] =
    useAlineMeasure<HTMLDivElement>();
  const [setDeliverablesRef, deliverablesMeasure] = useAlineMeasure<HTMLDivElement>();
  const bottomContainerHeight = bottomContainerMeasure?.height ?? 0;
  const bottomInputWidth = bottomInputMeasure?.width ?? 0;
  const deliverablesHeight = deliverablesMeasure?.height ?? 0;
  const pendingInteraction = usePendingInteraction(threadId);
  const scrollToBottomButtonBottom =
    bottomContainerHeight + (deliverablesHeight > 0 ? deliverablesHeight + 16 : 16);
  const bottomArtifactsMaxHeight = bottomInputElement
    ? Math.max(160, Math.floor(bottomInputElement.offsetTop - 4))
    : null;
  const closeSelectedPlaybookRun = useCallback(
    () => onSelectedPlaybookRunChange(null),
    [onSelectedPlaybookRunChange]
  );
  const selectedPlaybookRunForThread =
    selectedPlaybookRun && threadId && selectedPlaybookRun.threadId === threadId
      ? selectedPlaybookRun
      : null;
  const selectedPlaybookRunContextValue = useMemo(
    () => ({
      closePlaybookRun: closeSelectedPlaybookRun,
      selectPlaybookRun: onSelectedPlaybookRunChange,
      selectedPlaybookRun: selectedPlaybookRunForThread,
    }),
    [closeSelectedPlaybookRun, onSelectedPlaybookRunChange, selectedPlaybookRunForThread]
  );
  const hasDocumentExtractionWait = useMemo(
    () =>
      messages.some(
        message =>
          message.role === Apollo.AgentMessageRole.Assistant &&
          getIsWaitingForDocumentExtraction(message)
      ),
    [messages]
  );

  useEffect(() => {
    // Stop scroll if there is a chat item in URL or not viewing a thread...
    if (state.autoScrollAIChatItem || !threadId) stopScroll();
  }, [state.autoScrollAIChatItem, threadId, stopScroll]);

  const onInsertSelectionMentionV3 = useCallback<InsertSelectionMentionV3Fn>(
    (editor, range, options) => {
      const inputRefToUse = threadId ? bottomInputRef : topInputRef;
      if (inputRefToUse.current)
        inputRefToUse.current.insertSelectionMentionV3(editor, range, options);
    },
    [threadId]
  );

  const onInsertDocumentMentions = useCallback(
    (mentions: DocumentMentionElement[]) => {
      const inputRefToUse = threadId ? bottomInputRef : topInputRef;
      return inputRefToUse.current?.insertMentions(mentions) ?? false;
    },
    [threadId]
  );

  const onRemoveDocumentMentions = useCallback(
    (ids: string[]) => {
      const inputRefToUse = threadId ? bottomInputRef : topInputRef;
      inputRefToUse.current?.removeDocumentMentions(ids);
    },
    [threadId]
  );

  const onGetDocumentChipIds = useCallback(() => {
    const inputRefToUse = threadId ? bottomInputRef : topInputRef;
    return inputRefToUse.current?.getDocumentChipIds() ?? [];
  }, [threadId]);

  const onInsertSkill = useCallback(
    (skill: PinnedSkill) => {
      const inputRefToUse = threadId ? bottomInputRef : topInputRef;
      return inputRefToUse.current?.insertSkill(skill) ?? false;
    },
    [threadId]
  );

  const insertIntoBottomPrompt = useCallback(({ mentions, skills }: PromptInsertion) => {
    const input = bottomInputRef.current;
    if (!input) return;
    input.insertMentions(mentions);
    for (const { scope, skill } of skills) input.insertSkill(skill, scope);
  }, []);

  const onSetPromptDraft = useCallback(
    (markdown: string) => {
      const inputRefToUse = threadId ? bottomInputRef : topInputRef;
      return inputRefToUse.current?.setPromptDraft(markdown) ?? false;
    },
    [threadId]
  );

  const sendUserMessage = useSendUserMessage({
    editorDocumentId: documentId ?? null,
    onNavigateToThread,
    threadId,
    visibleContext,
  });

  const setMeasuredBottomInputRef = useCallback(
    (element: HTMLDivElement | null) => {
      setBottomInputMeasureRef(element);
    },
    [setBottomInputMeasureRef]
  );

  useEffect(() => {
    /**
     * Only the singleton main chat host claims the shared callback slot; see
     * prop docs. Skipped for an unavailable thread: the composer is unmounted,
     * so "Add to Chat" would post to a dead `threadId` and queued mentions would
     * be dropped instead of draining into a later panel.
     */
    if (!registerGlobalCallbacks || isThreadUnavailable) return;

    $chatCallbacks.getDocumentChipIds = onGetDocumentChipIds;
    $chatCallbacks.insertDocumentMentions = onInsertDocumentMentions;
    $chatCallbacks.insertSelectionMentionV3 = onInsertSelectionMentionV3;
    $chatCallbacks.removeDocumentMentions = onRemoveDocumentMentions;
    $chatCallbacks.sendUserMessage = sendUserMessage;
    $chatCallbacks.setPromptDraft = onSetPromptDraft;
    $chatCallbacks.insertSkill = onInsertSkill;

    /**
     * Drain mentions queued before this panel mounted. Clear the queue only
     * after a confirmed insert — if the input's editor isn't ready yet the
     * insert no-ops, so we leave them queued for a later run of this effect
     * (e.g. when threadId changes) rather than dropping them.
     */
    const pending = $chatCallbacks.pendingDocumentMentions;
    if (pending && onInsertDocumentMentions(pending)) {
      $chatCallbacks.pendingDocumentMentions = null;
    }

    /**
     * Same for a prompt drafted while this pane was hidden. This effect also
     * re-runs when the pane comes back into view, which is exactly when the
     * editor's playbook drawer expects its prompt to appear.
     */
    const pendingDraft = $chatCallbacks.pendingPromptDraft;
    if (pendingDraft !== null && onSetPromptDraft(pendingDraft)) {
      $chatCallbacks.pendingPromptDraft = null;
    }

    const pendingSkill = $chatCallbacks.pendingSkill;
    if (pendingSkill && onInsertSkill(pendingSkill)) $chatCallbacks.pendingSkill = null;

    return () => {
      $chatCallbacks.insertSkill = null;
      // Relinquish the slot on unmount so out-of-tree callers queue work instead.
      $chatCallbacks.getDocumentChipIds = null;
      $chatCallbacks.insertDocumentMentions = null;
      $chatCallbacks.insertSelectionMentionV3 = null;
      $chatCallbacks.removeDocumentMentions = null;
      $chatCallbacks.sendUserMessage = null;
      $chatCallbacks.setPromptDraft = null;
    };
  }, [
    isThreadUnavailable,
    registerGlobalCallbacks,
    onGetDocumentChipIds,
    onInsertDocumentMentions,
    onInsertSelectionMentionV3,
    onRemoveDocumentMentions,
    onSetPromptDraft,
    onInsertSkill,
    sendUserMessage,
  ]);

  useDocPlaybookRunSubscriptions({ messages });
  usePlaybookGenerationSubscriptions({ messages });

  useEffect(() => {
    if (mode === 'page' && !documentId && typeof thread?.title?.trim() === 'string')
      updateActiveTabTitle(thread.title.trim() || 'Untitled Chat');
  }, [documentId, mode, thread?.title, updateActiveTabTitle]);

  return (
    <SelectedPlaybookRunContext.Provider value={selectedPlaybookRunContextValue}>
      {!documentId && (
        <Helmet>
          <title>{thread?.title?.trim() || 'New Chat'}</title>
        </Helmet>
      )}
      <React.Activity mode={selectedPlaybookRunForThread ? 'hidden' : 'visible'}>
        <div className={clsx(classes.root, className)}>
          {/*
            Rendered even when the thread is gone. Embedded hosts (workflow
            panel, chat drawer) hold `threadId` in state with no route to
            navigate away from, so the header's "new chat" action resets them.
          */}
          {threadId && !hideThreadHeader && (
            <div className={classes.headerContainer}>
              <ChatPanelHeader
                className={classes.header}
                isThreadNotFound={isNotFound}
                threadId={threadId}
                onCreateNewChat={onCreateNewChat}
                onGoBack={onThreadHeaderGoBack ?? onCreateNewChat}
              />
            </div>
          )}
          {hasDocumentExtractionWait && (
            <div className={classes.documentExtractionWaitContainer}>
              <DocumentExtractionWaitBanner className={classes.documentExtractionWaitBanner} />
            </div>
          )}
          <div
            ref={scrollRef}
            className={clsx(classes.scrollContainer, scrollContainerClassName)}
            /**
             * Reserve room at the bottom so the artifacts bar (collapsed
             * or expanded) doesn't cover the tail of the last assistant
             * message when scrolled to the bottom. The `+ 24` adds visual
             * breathing room between the message's action buttons and the
             * bar.
             */
            style={threadId && deliverablesHeight > 0 ? { paddingBottom: 24 } : undefined}
          >
            <ChatScrollContent
              ref={contentRef}
              accessRequestStatusMap={accessRequestStatusMap}
              document={document}
              documentId={documentId}
              hasLoadError={hasLoadError}
              hideFeatureCards={hideFeatureCards}
              inputRef={topInputRef}
              isNotFound={isNotFound}
              logoClassName={classes.logo}
              mentionEntityContextValue={mentionEntityContextValue}
              messages={messages}
              messagesContainerClassName={classes.messagesContainer}
              newChatWrapperBottomClassName={classes.newChatWrapperBottom}
              newChatWrapperCenteredClassName={classes.newChatWrapperCentered}
              newChatWrapperClassName={classes.newChatWrapper}
              notFoundActions={notFoundActions}
              pinInputToBottom={pinInputToBottom}
              refetchAccessRequestStatus={refetchAccessRequestStatus}
              threadId={threadId}
              updateAccessRequestStatus={updateAccessRequestStatus}
              visibleContext={visibleContext}
              onNavigateToThread={onNavigateToThread}
              onSubmit={sendUserMessage}
            />
          </div>
          {threadId && !isThreadUnavailable && !isAtBottom && (
            <Fab
              className={classes.scrollToBottomButton}
              size="small"
              style={{ bottom: scrollToBottomButtonBottom }}
              variant="circular"
              onClick={() => scrollToBottom()}
            >
              <DownArrowIcon />
            </Fab>
          )}
          <Popper
            disablePortal
            anchorEl={bottomInputElement}
            className={classes.bottomDeliverablesPopper}
            modifiers={[{ name: 'preventOverflow', options: { padding: 16 } }]}
            open={Boolean(
              threadId &&
              !isThreadUnavailable &&
              bottomInputElement &&
              (pendingInteraction || (thread?.artifacts?.length ?? 0) > 0)
            )}
            placement="top"
            style={bottomInputWidth > 0 ? { width: bottomInputWidth } : undefined}
          >
            {threadId && (
              <ChatPanelPopperBody
                ref={setDeliverablesRef}
                artifacts={thread?.artifacts ?? []}
                className={classes.bottomArtifacts}
                maxHeight={bottomArtifactsMaxHeight}
                pendingInteraction={pendingInteraction}
                threadId={threadId}
                onInsertIntoPrompt={insertIntoBottomPrompt}
              />
            )}
          </Popper>
          {threadId && !isThreadUnavailable && (
            <div ref={setBottomContainerRef} className={classes.bottomInputContainer}>
              <ChatPanelInput
                ref={bottomInputRef}
                className={classes.bottomInput}
                messages={messages}
                rootRef={setMeasuredBottomInputRef}
                threadId={threadId}
                visibleContext={visibleContext}
                onNavigateToThread={onNavigateToThread}
                onSubmit={sendUserMessage}
                onThreadDeleted={handleThreadDeleted}
              />
            </div>
          )}
        </div>
      </React.Activity>
      <React.Activity mode={selectedPlaybookRunForThread ? 'visible' : 'hidden'}>
        {selectedPlaybookRunForThread && (
          <div
            className={clsx(classes.root, mode === 'page' && classes.playbookRunPage, className)}
          >
            <ChatPlaybookRunPanel
              documentId={selectedPlaybookRunForThread.documentId}
              playbookId={selectedPlaybookRunForThread.playbookId}
              onBack={closeSelectedPlaybookRun}
            />
          </div>
        )}
      </React.Activity>
    </SelectedPlaybookRunContext.Provider>
  );
}

void gql`
  query GetChatDocument($documentId: UUID!) {
    document(documentId: $documentId) {
      __typename
      id
      name
      latestVersion {
        ... on DocumentVersion {
          __typename
          id
        }
        ... on StaticFileVersion {
          __typename
          id
          fileLocation {
            __typename
            mimetype
          }
        }
      }
    }
  }
`;

function useMaybeDocument() {
  const { documentId } = useMaybeDocumentParams();
  const { data, loading } = Apollo.useGetChatDocumentQuery({
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });

  return { document: data?.document ?? null, documentId, loading };
}
